import React, { useRef, useEffect, useState } from 'react';
import { Navbar, Nav, NavDropdown, Container, Badge } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiBell, FiSettings, FiUsers, FiClipboard, FiLogOut, FiHome, FiChevronDown, FiMenu, FiTrendingUp } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import quotationsService from '../../services/quotations.service';
import companyService from '../../services/company.service';
import './GlobalHeader.css';

const GlobalHeader = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { refreshTrigger } = useNotifications();
    const [pendingCount, setPendingCount] = useState(0);
    const [companyData, setCompanyData] = useState({ name: '', logo: null });
    const [indicatorStyle, setIndicatorStyle] = useState({ opacity: 0 });
    const [isReady, setIsReady] = useState(false);
    const navRef = useRef(null);

    useEffect(() => {
        if (user) {
            loadStats();
            companyService.getMyCompany()
                .then(data => setCompanyData({ name: data.razon_social || data.nombre || '', logo: data.logo || null }))
                .catch(err => console.error("Error loading company:", err));

            const interval = setInterval(loadStats, 30000);
            return () => clearInterval(interval);
        }
    }, [user, refreshTrigger]);

    useEffect(() => {
        const updateIndicator = () => {
            if (!navRef.current) return;
            // Solo para las pantallas grandes donde apply d-lg-flex
            const activeLink = navRef.current.querySelector('.nav-item-link.active');
            if (activeLink && window.innerWidth >= 992) {
                setIndicatorStyle({
                    left: activeLink.offsetLeft,
                    width: activeLink.offsetWidth,
                    height: activeLink.offsetHeight,
                    top: activeLink.offsetTop,
                    opacity: 1
                });
                if (!isReady) setTimeout(() => setIsReady(true), 50);
            } else {
                setIndicatorStyle({ opacity: 0 });
                setIsReady(false);
            }
        };

        const timeout = setTimeout(updateIndicator, 100);
        window.addEventListener('resize', updateIndicator);

        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', updateIndicator);
        };
    }, [location.pathname, user?.rol, isReady]);

    const loadStats = async () => {
        try {
            const data = await quotationsService.getPendingStats();
            setPendingCount(data.count !== undefined ? data.count : (data.pendientes || 0));
        } catch (err) {
            console.error('Error loading stats:', err);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    if (!user) return null; // Solo se muestra si está autenticado

    const isActive = (path) => location.pathname.startsWith(path);

    // Módulos principales a los que aplica el Header pero que podrían tener un Sidebar secundario
    const isAppSection = location.pathname.startsWith('/app/cotizaplus') || location.pathname.startsWith('/app/crm') || location.pathname.startsWith('/app/inventario');
    const isGlobalContext = !isAppSection; // Si estamos en el home (/) o configuracion (/app/configuracion), etc.

    let appName = 'IdevPlus';
    if (location.pathname.startsWith('/app/cotizaplus')) appName = 'CotizaPlus';
    else if (location.pathname.startsWith('/app/crm')) appName = 'CRM Plus';
    else if (location.pathname.startsWith('/app/inventario')) appName = 'Inventario Plus';

    const displayCompanyName = companyData.name || (user.first_name ? `${user.first_name} ${user.last_name}` : 'IdevPlus');
    const displayInitial = displayCompanyName.charAt(0).toUpperCase();

    return (
        <Navbar expand="lg" className="global-header shadow-sm" sticky="top">
            <Container fluid className="px-3 px-md-4">
                <div className="d-flex align-items-center">
                    {/* Botón Hamburguesa Móvil (A la izquierda del logo) */}
                    {isAppSection && (
                        <button
                            className="bg-transparent border-0 p-0 text-dark me-3 d-lg-none"
                            onClick={() => window.dispatchEvent(new CustomEvent('toggleMobileSidebar'))}
                        >
                            <FiMenu size={26} />
                        </button>
                    )}

                    <Navbar.Brand as={Link} to="/" className="global-logo-container m-0">
                        <span className="global-logo-text">{appName}</span>
                        {appName !== 'IdevPlus' && <span className="global-logo-subtitle d-none d-sm-block ms-1">by IdevPlus</span>}
                    </Navbar.Brand>
                </div>

                <div className="d-flex align-items-center ms-auto">
                    {/* Links de Desktop */}
                    <Nav className="d-none d-lg-flex align-items-center position-relative me-3" ref={navRef}>
                        {/* Indicador animado (Opcional, desktop only) */}
                        <div className={`nav-active-indicator d-none d-lg-block ${isReady ? 'ready' : ''}`} style={indicatorStyle}></div>

                        <Nav.Link as={Link} to="/" id="nav-inicio" className={`nav-item-link ${location.pathname === '/' ? 'active' : ''}`}>
                            <FiHome className="me-2" /> Inicio
                        </Nav.Link>
                        {isGlobalContext && (
                            <>
                                <Nav.Link as={Link} to="/app/solicitudes" id="nav-solicitudes" className={`nav-item-link ${isActive('/app/solicitudes') ? 'active' : ''}`}>
                                    <FiClipboard className="me-2" /> Solicitudes
                                </Nav.Link>
                                {user?.rol === 'ADMIN' && (
                                    <>
                                        <Nav.Link as={Link} to="/app/empleados" id="nav-empleados" className={`nav-item-link ${isActive('/app/empleados') ? 'active' : ''}`}>
                                            <FiUsers className="me-2" /> Empleados
                                        </Nav.Link>
                                        <Nav.Link as={Link} to="/app/configuracion" id="nav-configuraciones" className={`nav-item-link ${isActive('/app/configuracion') ? 'active' : ''}`}>
                                            <FiSettings className="me-2" /> Configuraciones
                                        </Nav.Link>
                                    </>
                                )}
                            </>
                        )}
                    </Nav>

                    {/* Notificaciones Siempre Visibles A la Derecha */}
                    {isAppSection && (
                        <div
                            className="notification-link me-3 d-flex align-items-center"
                            onClick={() => navigate('/app/cotizaplus/cotizaciones?pending=true')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="position-relative text-secondary">
                                <FiBell size={22} />
                                {pendingCount > 0 && (
                                    <Badge bg="danger" pill className="notification-badge" style={{ position: 'absolute', top: '-5px', right: '-8px' }}>
                                        {pendingCount}
                                    </Badge>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Menú Desplegable Avatar */}
                    <NavDropdown
                        title={
                            <span className="user-dropdown-title">
                                {companyData.logo ? (
                                    <img src={companyData.logo} alt="Logo" className="user-avatar" style={{ objectFit: 'cover', backgroundColor: 'white' }} />
                                ) : (
                                    <span className="user-avatar">{displayInitial}</span>
                                )}
                                <span className="user-name d-none d-xl-inline fw-semibold ms-2">{displayCompanyName}</span>
                                <FiChevronDown className="d-none d-xl-inline text-muted ms-1" size={16} />
                            </span>
                        }
                        id="user-nav-dropdown"
                        align="end"
                        className="user-dropdown m-0 p-0"
                    >
                        {/* En móvil: siempre mostrar todos los enlaces en el dropdown */}
                        {/* En desktop: solo mostrar enlaces si estamos dentro de un módulo (CotizaPlus, etc.) */}
                        <NavDropdown.Item as={Link} to="/" className="d-lg-none">
                            <FiHome className="me-2 text-muted" /> Inicio
                        </NavDropdown.Item>
                        <NavDropdown.Item as={Link} to="/app/solicitudes" className={isGlobalContext ? 'd-lg-none' : ''}>
                            <FiClipboard className="me-2 text-muted" /> Solicitudes
                        </NavDropdown.Item>
                        {user?.rol === 'ADMIN' && (
                            <>
                                <NavDropdown.Item as={Link} to="/app/empleados" className={isGlobalContext ? 'd-lg-none' : ''}>
                                    <FiUsers className="me-2 text-muted" /> Empleados
                                </NavDropdown.Item>
                                <NavDropdown.Item as={Link} to="/app/configuracion" className={isGlobalContext ? 'd-lg-none' : ''}>
                                    <FiSettings className="me-2 text-muted" /> Configuraciones
                                </NavDropdown.Item>
                            </>
                        )}
                        {user?.rol === 'ADMIN' && (
                            <>
                                <NavDropdown.Divider />
                                <NavDropdown.Item as={Link} to="/pricing" className="fw-medium text-primary">
                                    <FiTrendingUp className="me-2" /> Mi Suscripción / Planes
                                </NavDropdown.Item>
                                <NavDropdown.Divider />
                            </>
                        )}

                        {/* Info de Perfil */}
                        <div className="px-3 py-2 border-bottom mb-2 text-center text-lg-start">
                            <p className="mb-0 fw-bold">{user.first_name} {user.last_name}</p>
                            <p className="mb-0 text-muted small text-truncate" style={{ maxWidth: '200px' }}>{user.email}</p>
                        </div>

                        <NavDropdown.Item onClick={handleLogout} className="text-danger fw-medium">
                            <FiLogOut className="me-2" /> Cerrar Sesión
                        </NavDropdown.Item>
                    </NavDropdown>
                </div>
            </Container>
        </Navbar>
    );
};

export default GlobalHeader;
